import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Quote, Award, Newspaper, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
const PILLARS = [{
  title: 'Experience Silent Travel',
  description: 'Whisper-quiet campervan journeys designed for refined long-distance comfort.',
  image: 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg',
  slug: 'silent-travel'
}, {
  title: 'Air Furniture Technology™',
  description: 'Lightweight structural furniture engineered for smarter travel efficiency.',
  image: 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp',
  slug: 'air-furniture'
}, {
  title: 'Premium Interiors',
  description: 'Hand-crafted luxury with meticulous material selection and upholstery precision.',
  image: 'https://cdn.magicpatterns.com/uploads/vBzCByw2N7rP6MS6jLeNiZ/_DSC8493.webp',
  slug: 'premium-interiors'
}, {
  title: '16,000 Customization Options',
  description: 'Create a truly personalized campervan tailored to your travel style.',
  image: 'https://cdn.magicpatterns.com/uploads/7z3is2BCgabuUutCmURgeJ/_DSC8265.webp',
  slug: 'customization'
}, {
  title: 'Signature Craftsmanship',
  description: 'Our "one van, one craftsman" philosophy ensures accountability and exceptional quality.',
  image: 'https://cdn.magicpatterns.com/uploads/pPR23bNLSAVudMWhF2ynaG/_DSC8241.webp',
  slug: 'signature-craftsmanship'
}];
export function RobetaDifferencePage() {
  useSEO({
    title: 'The Robeta Difference — What Sets Us Apart',
    description: 'Five principles that define every Robeta camper van: Silent Travel, Air Furniture Technology, Premium Interiors, 16,000 Customization Options, and Signature Craftsmanship.'
  });
  return <main className="bg-white min-h-screen pt-32 md:pt-40" data-id="element-1154">
      {/* 1. Page Header */}
      <section className="container mx-auto px-6 md:px-12 mb-20 md:mb-28" data-id="element-1155">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="max-w-4xl" data-id="element-1156">
          <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-1157">
            What Sets Us Apart
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial leading-tight mb-6" data-id="element-1158">
            The Robeta Difference
          </h1>
          <p className="text-lg md:text-xl font-light text-robeta-black/60 max-w-2xl leading-relaxed" data-id="element-1159">
            Five principles that define every Robeta camper van. No compromise.
            No shortcuts. Only intention.
          </p>
        </motion.div>
      </section>

      {/* 2. Five Core Pillars Grid */}
      <section className="container mx-auto px-6 md:px-12 mb-28 md:mb-36" data-id="element-1160">
        {/* Row 1: 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-1161">
          {PILLARS.slice(0, 3).map((pillar, index) => <PillarCard key={index} pillar={pillar} index={index} data-id="element-1162" />)}
        </div>

        {/* Row 2: 2 columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1163">
          {PILLARS.slice(3).map((pillar, index) => <PillarCard key={index + 3} pillar={pillar} index={index + 3} data-id="element-1164" />)}
        </div>
      </section>

      {/* 3. Design Philosophy Statement */}
      <section className="py-28 md:py-36 border-t border-robeta-black/5" data-id="element-1165">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1166">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="max-w-5xl mx-auto text-center" data-id="element-1167">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-8" data-id="element-1168">
              Design Philosophy
            </span>
            <blockquote className="text-3xl md:text-5xl lg:text-6xl font-light text-robeta-black uppercase tracking-editorial leading-tight mb-8" data-id="element-1169">
              Precision is not a detail.
              <br data-id="element-1170" />
              <span className="text-robeta-black/40" data-id="element-1171">It is the design.</span>
            </blockquote>
            <p className="text-robeta-black/50 font-light text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-1172">
              Every Robeta camper van is the result of European engineering
              discipline — where intention guides every decision, and nothing
              exists without purpose.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 4. TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-white" data-id="element-1173">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1174">
          <div className="text-center mb-16" data-id="element-1175">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-1176">
              Owner Stories
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1177">
              What Owners Say
            </h2>
            <div className="w-24 h-px bg-robeta-copper mx-auto" data-id="element-1178" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto" data-id="element-1179">
            {[{
            quote: 'The silence on the highway is unreal. We drove from Toronto to Banff and arrived feeling rested, not exhausted. Nothing else in this class comes close.',
            name: 'Sarah & Mike T.',
            location: 'Ontario, Canada',
            model: 'Mythos 4 Two'
          }, {
            quote: 'Coming from a traditional RV, the build quality is night and day. Every drawer, every surface — it all feels like it belongs in a luxury apartment, not a van.',
            name: 'James D.',
            location: 'British Columbia, Canada',
            model: 'Mythos 4 Two'
          }, {
            quote: 'We spent three weeks off-grid in Joshua Tree. The solar and electrical systems never missed a beat. This van is engineered for real adventure.',
            name: 'Elena R.',
            location: 'California, USA',
            model: 'Mythos 4 Two'
          }].map((testimonial, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="p-8 border border-robeta-black/5 flex flex-col" data-id="element-1180">
                <Quote className="w-8 h-8 text-robeta-copper/30 mb-6" data-id="element-1181" />
                <p className="text-robeta-black/70 font-light leading-relaxed mb-8 flex-grow" data-id="element-1182">
                  "{testimonial.quote}"
                </p>
                <div className="border-t border-robeta-black/5 pt-6" data-id="element-1183">
                  <p className="text-sm font-bold text-robeta-black uppercase tracking-wide" data-id="element-1184">
                    {testimonial.name}
                  </p>
                  <p className="text-xs text-robeta-black/40 mt-1" data-id="element-1185">
                    {testimonial.location} · {testimonial.model}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 5. PRESS & AWARDS */}
      <section className="py-24 md:py-32 bg-robeta-mist/10" data-id="element-1186">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1187">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24" data-id="element-1188">
            <div className="w-full lg:w-1/3" data-id="element-1189">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-1190">
                Recognition
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1191">
                Press & Awards
              </h2>
              <p className="text-robeta-black/60 font-light leading-relaxed" data-id="element-1192">
                Robeta's commitment to innovation and quality has been
                recognized across the industry.
              </p>
            </div>
            <div className="w-full lg:w-2/3 space-y-6" data-id="element-1193">
              {[{
              icon: Award,
              title: 'European Innovation Award 2025',
              source: 'Caravan Salon Düsseldorf',
              desc: 'Recognized for Air Furniture Technology™ as a breakthrough in lightweight campervan construction.'
            }, {
              icon: Newspaper,
              title: '"The Quietest Van We\'ve Ever Tested"',
              source: 'Camper Van Magazine, 2025',
              desc: "Independent road test confirmed Robeta's class-leading acoustic insulation and ride refinement."
            }, {
              icon: Award,
              title: 'Best Interior Design — Class B',
              source: 'North American RV Awards 2026',
              desc: 'Awarded for yacht-inspired interior design, material quality, and ergonomic innovation.'
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="flex gap-6 p-6 bg-white border border-robeta-black/5" data-id="element-1194">
                  <item.icon className="w-8 h-8 text-robeta-copper flex-shrink-0 mt-1" data-id="element-1195" />
                  <div data-id="element-1196">
                    <h3 className="text-sm font-bold text-robeta-black uppercase tracking-wide mb-1" data-id="element-1197">
                      {item.title}
                    </h3>
                    <p className="text-xs text-robeta-copper font-bold uppercase tracking-widest mb-2" data-id="element-1198">
                      {item.source}
                    </p>
                    <p className="text-sm text-robeta-black/60 font-light leading-relaxed" data-id="element-1199">
                      {item.desc}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 6. WARRANTY & AFTERSALES */}
      <section className="py-24 md:py-32 bg-white" data-id="element-1200">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1201">
          <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-1202">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-1203">
              Peace of Mind
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-1204">
              Warranty & Aftersales
            </h2>
            <p className="text-robeta-black/60 font-light text-lg max-w-2xl mx-auto leading-relaxed" data-id="element-1205">
              Your Robeta is built to last — and we stand behind every vehicle
              with comprehensive coverage and dedicated support.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-1206">
            {[{
            title: 'Structural Warranty',
            duration: '5 Years',
            desc: 'Full coverage on the conversion structure, Air Furniture panels, and all fixed installations.'
          }, {
            title: 'Systems Warranty',
            duration: '3 Years',
            desc: 'Electrical, plumbing, heating, and all integrated systems covered against manufacturing defects.'
          }, {
            title: 'Personal Assistant',
            duration: 'Lifetime',
            desc: 'Every Robeta owner gets lifetime access to our dedicated concierge support team for any question.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className="text-center p-8 border border-robeta-black/5" data-id="element-1207">
                <ShieldCheck className="w-10 h-10 text-robeta-copper mx-auto mb-6" data-id="element-1208" />
                <span className="block text-3xl font-bold text-robeta-black uppercase tracking-editorial mb-2" data-id="element-1209">
                  {item.duration}
                </span>
                <h3 className="text-sm font-bold text-robeta-black uppercase tracking-wide mb-4" data-id="element-1210">
                  {item.title}
                </h3>
                <p className="text-sm text-robeta-black/60 font-light leading-relaxed" data-id="element-1211">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 7. Conversion CTA */}
      <section className="py-28 md:py-36 bg-robeta-black text-white text-center" data-id="element-1212">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1213">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1214">
            <h2 className="text-4xl md:text-6xl font-bold uppercase tracking-editorial mb-12" data-id="element-1215">
              Experience the
              <br data-id="element-1216" />
              Robeta Standard
            </h2>
            <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-1217">
              <Link to="/dealer-network" className="px-10 py-4 bg-robeta-copper text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors min-w-[200px]" data-id="element-1218">
                Find a Dealer
              </Link>
              <Link to="/contact" className="px-10 py-4 border border-white text-white font-bold uppercase tracking-widest hover:bg-white/10 transition-colors min-w-[200px]" data-id="element-1219">
                Request Info
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>;
}
function PillarCard({
  pillar,
  index
}: {
  pillar: (typeof PILLARS)[0];
  index: number;
}) {
  return <Link to={`/robeta-difference/${pillar.slug}`} data-id="element-1220">
      <motion.div initial={{
      opacity: 0,
      y: 30
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.6,
      delay: index * 0.1
    }} className="relative aspect-[4/3] overflow-hidden group cursor-pointer" data-id="element-1221">
        <img src={pillar.image} alt={pillar.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-1222" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/5" data-id="element-1223" />

        <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-10" data-id="element-1224">
          <h3 className="text-robeta-copper text-xl md:text-2xl font-bold uppercase tracking-editorial mb-3 leading-snug" data-id="element-1225">
            {pillar.title}
          </h3>
          <p className="text-white/80 text-sm font-light leading-relaxed mb-6 max-w-sm" data-id="element-1226">
            {pillar.description}
          </p>
          <div data-id="element-1227">
            <span className="inline-flex items-center text-white/60 text-[11px] font-bold uppercase tracking-widest group-hover:text-robeta-copper transition-colors" data-id="element-1228">
              Read More
              <ArrowRight className="ml-2 w-4 h-4 transform group-hover:translate-x-1 transition-transform" data-id="element-1229" />
            </span>
          </div>
        </div>
      </motion.div>
    </Link>;
}